<template>
  <v-container fluid>

    <!-- Page Title -->
    <app-page-title
      v-bind:title="'Bull Finder Queries'"
      v-bind:icon="'query_builder'"
      v-bind:actionLink="!isSmallDevice ? 'Go To Bull Finder' : 'Bull Finder'"
      v-bind:actionLinkHidden="isLoading"
      @action-link-click="actionLinkClicked"
    />

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="queries"
      :loading="isLoading"
      :search="search"
      :no-data-text="noDataText"
      item-key="id"
      mobile-breakpoint="500"
      >
      <v-progress-linear v-slot:progress :color="appColor_Progress" indeterminate />

      <!-- Header Toolbar -->
      <template v-slot:top>
        <v-toolbar dark :color="appColor_Medium" :height="isAdminy ? '64' : 'auto'">

          <!-- Customer Filter -->
          <v-toolbar-items class="pt-1">
            <bb-customer-dropdown
              v-if="isAdminy"
              v-model="userId"
              v-bind:profile="profile"
              @load="customersLoaded"
              @change="loadQueries()"
              @error="customerError"
            />
          </v-toolbar-items>

          <!-- Search (Large Device) -->
          <v-text-field
            v-if="(!isSmallDevice && isAdminy) || !isAdminy"
            v-model="search"
            append-icon="search"
            label="Search"
            single-line
            hide-details
            clearable
            class="mt-2 ml-3"
          />

          <v-spacer />

          <!-- Edit Dialog -->
          <v-dialog v-model="editDialog"
            max-width="550px"
            :persistent="true"
            >
            <template v-slot:activator="{ on }">
              <v-btn
                :small="isSmallDevice"
                outlined
                v-on="on"
                :disabled="isLoading"
                class="ml-3"
                >
                New Query
              </v-btn>
            </template>
            <v-card>

              <!-- Toolbar -->
              <v-toolbar dark flat :color="appColor_Light">
                <v-icon class="mr-2">
                  query_builder
                </v-icon> 
                <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
                <v-spacer />
                <v-chip v-if="isEditMode"
                  :color="getAnimalSourceColourCode(editedQuery.animalSourceId)"
                >{{ getAnimalSourceCosmosCode(editedQuery.animalSourceId) }}</v-chip>
              </v-toolbar>

              <!-- Form -->
              <v-card-text class="pb-0 pt-3 py-2">
                <v-container grid-list-md>
                  <form>

                    <!-- Animal Source -->
                    <template>
                      <v-row no-gutters>
                        <v-col
                          cols="12"
                          sm="12"
                          md="12"
                          >
                          <v-select
                            v-model="editedQuery.animalSourceId"
                            :items="animalSourceItems"
                            label="Source"
                            placeholder="Select Source"
                            v-validate="'required'"
                            :error-messages="errors.collect('animalSource')"
                            data-vv-name="animalSource"
                            @change="animalSourceChanged()"
                            :disabled="animalSourceDisabled"
                            outlined
                            chips
                            class="pb-4"
                            hide-details="auto"
                            >
                            <template v-slot:selection="{ item }">
                              <v-chip
                                dark
                                :color="selectedAnimalColourCode"
                                :disabled="animalSourceDisabled"
                                >
                                {{ item.text }}
                              </v-chip>
                            </template>
                          </v-select>
                        </v-col>
                      </v-row>
                    </template>

                    <!-- Radio Buttons - Custom/FromTemplate -->
                    <v-row no-gutters>
                      <v-col
                        cols="12"
                        sm="12"
                        md="12"
                        v-if="isAddMode"
                        >
                        <v-radio-group
                          row
                          class="mt-3 ml-1 pb-4"
                          v-model="editedQuery.queryTypeId"
                          @change="queryTypeChanged"
                          :disabled="queryTypeDisabled"
                          hide-details
                          >
                          <v-radio
                            :value="queryTypeId_Custom"
                            :color="appColor_Selected"
                            label="Custom"
                            >
                          </v-radio>
                          <v-radio
                            :value="queryTypeId_FromTemplate"
                            :color="appColor_Selected"
                            label="From Template"
                            :disabled="sourceTemplates.length === 0"
                            >
                          </v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>

                    <!-- Template -->
                    <template v-if="isAddMode && isTemplateSelected">
                      <v-row no-gutters>
                        <v-col
                          cols="12"
                          sm="12"
                          md="12"
                          >
                          <v-select
                            v-model="editedQuery.templateId" 
                            :items="sourceTemplates"
                            label="Template Name"
                            placeholder="Select Template"
                            :hint="editedQuery.templateDesc"
                            persistent-hint
                            v-validate="isTemplateSelected ? 'required' : ''"
                            :error-messages="errors.collect('template')"
                            data-vv-name="template"
                            :disabled="templateDisabled"
                            @change="templateChanged"
                            class="pb-2"
                          ></v-select>
                        </v-col>
                      </v-row>
                    </template>

                    <!-- Breed / Year (from template) -->
                    <template v-if="isAddMode && isTemplateSelected">
                      <v-row no-gutters>
                        <!-- Breed -->
                        <v-col
                          cols="12"
                          sm="8"
                          md="8"
                          >
                          <v-alert outlined color="grey" class="mb-4 py-3" :value="isTemplate">
                            <v-chip>{{ getBreedName(editedQuery.animalSourceId, editedQuery.breedId) }}</v-chip>
                            <v-icon :color="appColor_Selected" class="ml-1">done</v-icon>
                          </v-alert>
                        </v-col>

                        <!-- Birth Year -->
                        <v-col
                          cols="12"
                          sm="4"
                          md="4"
                          >
                          <v-alert outlined color="grey" class="ml-4 mb-3 py-3" :value="isTemplate">
                            <v-chip>{{ editedQuery.birthYear }}</v-chip>
                            <v-icon :color="appColor_Selected" class="ml-1">done</v-icon>
                          </v-alert>
                        </v-col>
                      </v-row>
                    </template>

                    <!-- Breed / Year (custom) -->
                    <template v-if="editedQuery.queryTypeId === queryTypeId_Custom || isEditMode">
                      <v-row no-gutters>
                        <!-- Breed  -->
                        <v-col
                          cols="12"
                          sm="8"
                          md="8"
                          >
                          <v-select
                            v-model="editedQuery.breedId"
                            v-validate="!isTemplateSelected ? 'required' : ''"
                            :error-messages="errors.collect('breed')"
                            data-vv-name="breed"                            
                            :items="breeds"
                            label="Breed"
                            placeholder="Select Breed"
                            :disabled="breedDisabled"
                            chips
                            hide-details="auto"
                            @change="breedOrYearChanged"
                            class="pt-4 pb-4"
                          />
                        </v-col>

                        <!-- Birth Year -->
                        <v-col
                          cols="12"
                          sm="4"
                          md="4"
                          >
                          <v-select
                            v-if="!isTemplateSelected || isEditMode"
                            v-model="editedQuery.birthYear"
                            v-validate="!isTemplateSelected ? 'required' : ''"
                            :error-messages="errors.collect('birthYear')"
                            data-vv-name="birthYear"
                            :items="birthYears"
                            label="Birth Year"
                            placeholder="Select Year"
                            :disabled="birthYearDisabled"
                            chips
                            hide-details="auto"
                            @change="breedOrYearChanged"
                            class="pt-4 pb-4 ml-3"
                          />
                        </v-col>
                      </v-row>
                    </template>

                    <!-- Category -->
                    <v-row no-gutters>
                      <v-col
                        cols="12"
                        sm="12"
                        md="12"
                        >
                        <v-combobox
                          :value="editedQuery.category"
                          :items="categoryItems"
                          label="Category"
                          :disabled="categoryDisabled"
                          ref="category"
                          :hint="editedQuery.category === null ? 'Enter a category (optional)' : null"
                          persistent-hint
                          clearable
                          class="pt-5 pb-0"
                        ></v-combobox>
                      </v-col>
                    </v-row>

                    <!-- Title -->
                    <v-row>
                      <v-col
                        cols="12"
                        sm="12"
                        md="12"
                        >
                        <v-text-field
                          v-model="editedQuery.title"
                          v-validate="'max:50|required'"
                          :error-messages="errors.collect('title')"
                          data-vv-name="title"
                          label="Title"
                          :disabled="titleDisabled"
                          ref="title"
                          hide-details="auto"
                          clearable
                        />
                      </v-col>
                    </v-row>

                    <!-- Description -->
                    <v-row>
                      <v-col
                        cols="12"
                        sm="12"
                        md="12"
                        >
                        <v-text-field
                          v-model="editedQuery.description"
                          v-validate="'max:200'"
                          :error-messages="errors.collect('description')"
                          data-vv-name="description"
                          label="Description"
                          flat
                          no-resize
                          clearable
                          :disabled="titleDisabled"
                          hide-details
                        />
                      </v-col>
                    </v-row>
                  </form>
                </v-container>
              </v-card-text>

              <!-- Card Actions -->
              <v-card-actions class="py-4">

                <!-- Save As Template -->
                <v-checkbox
                  v-if="isAdmin && isEditMode"
                  label="Share As Template"
                  class="ml-6 mt-0 pt-0 pb-1"
                  :color="appColor_Selected"
                  v-model="editedQuery.isTemplate"
                  hint="Allow others to scaffold a query from this one"
                  persistent-hint
                  >
                </v-checkbox>

                <v-spacer />

                <!-- Action Buttons -->
                <v-btn
                  :disabled="isSaving"
                  text
                  @click="closeEditDialog"
                  >
                  Cancel
                </v-btn>
                <v-btn
                  color="primary"
                  :disabled="isSaving || saveDisabled"
                  text
                  @click="saveEdit"
                  >
                  Save
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

        </v-toolbar>

        <!-- Small Device Toolbar -->
        <v-toolbar
          v-if="isSmallDevice && isAdminy"
          light
          dark
          :color="appColor_Medium"
          >

          <v-text-field
            v-model="search"
            append-icon="search"
            label="Search"
            single-line
            dense
            hide-details
            clearable
          />
        </v-toolbar>
      </template>

      <template v-slot:item.details="{ item }">
        <v-btn icon @click="editDetail(item)">
          <v-icon>details</v-icon>
        </v-btn>
      </template>

      <template v-slot:item.title="{ item }">
        <router-link to="/bullFinderManager">
          <span @click="edit(item)">{{ item.title }}</span>
        </router-link>
        <v-chip v-if="item.isTemplate" outlined small class="caption ml-4 primary blue--text">Shared</v-chip>
      </template>

      <template v-slot:item.animalSourceId="{ item }">
        <v-chip dark small class="elevation-1"
          :color="getAnimalSourceColourCode(item.animalSourceId) + ' lighten-1'"
          >
          {{ getAnimalSourceCosmosCode(item.animalSourceId) }}
        </v-chip>
      </template>

      <template v-slot:item.breedId="{ item }">
        <span>{{ getBreedName(item.animalSourceId, item.breedId) }}</span>
      </template>

      <template v-slot:item.action="{ item }">
        <v-icon @click="remove(item)">delete</v-icon>
      </template>
    </v-data-table>

    <!-- Criteria Dialog -->
    <v-dialog
      v-model="detailDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      >
      <user-query-criteria
        v-bind:control-id="selectedQueryInfo.id"
        v-bind:query-info="selectedQueryInfo"
        v-bind:user-trait-ids="selectedTraitIds"
        v-bind:filter-criteria="filterCriteriaOriginal"
        v-bind:trait-criteria="traitCriteriaOriginal"
        v-bind:years="birthYears"
        v-bind:scope="scope"
        v-bind:is-add-mode="isAddMode"
        v-bind:is-breeder="isAdminy"
        @cancel="criteriaCancel"
        @save="criteriaSave"
      />
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog v-model="errorDialog" max-width="500px">
      <app-error
        :error-title="errorTitle"
        :error-text="errorText"
        @close="errorDialog = false"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import AppPageTitle from '@/components/AppPageTitle'
import AppError from '@/components/AppError'
import UserQueryCriteria from '@/components/UserQueryCriteria'
import BbCustomerDropdown from '@/components/BbCustomerDropdown'
import { mapGetters } from 'vuex'
import { AppColor, QueryTypeId, FilterName } from '@/config.js'

export default {
  name: 'BullFinderManager',
  $_veeValidate: {
    validator: 'new'
  },
  components: {
    AppPageTitle,
    AppError,
    UserQueryCriteria,
    BbCustomerDropdown
  },
  data () {
    return {
      appColor_Light: AppColor.Light,
      appColor_Medium: AppColor.Medium,
      appColor_Dark: AppColor.Dark,
      appColor_Background: AppColor.Background,
      appColor_PanelBackground: AppColor.PanelBackground,
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,

      // query type id
      queryTypeId_Custom: QueryTypeId.Custom,
      queryTypeId_Template: QueryTypeId.Template,
      queryTypeId_FromTemplate: QueryTypeId.FromTemplate,

      // filters
      filter_Breed: FilterName.Breed,
      filter_BirthYear: FilterName.BirthYear,

      scope: 'bullfinder',
      isLoading: false,
      isSaving: false,
      isUserHerd: false,
      editDialog: false,
      detailDialog: false,
      confirmDialog: false,
      search: '',
      filterCriteria: [],
      traitCriteria: [],
      queries: [],
      categories: [],
      animalSourcesTemplates: [],
      filterCriteriaOriginal: [],
      traitCriteriaOriginal: [],
      selectedTraitIds: [],
      animalSources: [],
      animalSourcesCriteria: [],
      animalSourcesBreeds: [],
      animalSourcesYears: [],
      breeds: [],
      birthYears: [],
      isGenderDisabled: false,

      // for the criteria picker
      selectedQueryInfo: {
        id: 0,
        title: null,
        description: null,
        colourCode: null,
        cosmosCode: null
      },

      headers: [
        {
          text: 'Detail',
          value: 'details',
          align: 'center',
          sortable: false,
          width: '5%'
        },
        {
          value: 'animalSourceId',
          align: 'left',
          width: '5%'
        },
        {
          text: 'Title',
          value: 'title',
          align: 'left',
          width: '25%'
        },
        {
          text: 'Description',
          value: 'description',
          align: 'left',
          width: '20%'
        },
        {
          text: 'Category',
          value: 'category',
          align: 'left',
          width: '10%'
        },
        {
          text: 'Breed',
          value: 'breedId',
          align: 'left',
          width: '13%'
        },
        {
          text: 'Year',
          value: 'birthYear',
          align: 'left',
          width: '5%'
        },
        {
          value: 'action',
          sortable: false,
          width: '2%'
        }
      ],
      editedIndex: -1,
      defaultQuery: {
        id: -1,
        queryTypeId: QueryTypeId.Custom,
        userId: null,
        title: '',
        description: '',
        animalSourceId: '',
        templateId: '',
        templateDesc: '',
        category: null,
        birthYear: '',
        breedId: '',
        isTemplate: false
      },
      editedQuery: {
        id: -1,
        queryTypeId: QueryTypeId.Custom,
        userId: null,
        title: '',
        description: '',
        animalSourceId: '',
        templateId: '',
        templateDesc: '',
        category: null,
        birthYear: '',
        breedId: '',
        isTemplate: false
      },
      dictionary: {
        custom: {
          animalSource: {
            required: () => 'Animal Source is required'
          },
          template: {
            required: () => 'Template is required'
          },
          title: {
            required: () => 'Title is required',
            max: 'Title cannot be greater than 50 characters'
          },
          description: {
            max: 'Description cannot be greater than 200 characters'
          },
          breed: {
            required: () => 'Breed is required'
          },
          birthYear: {
            required: () => 'Birth Year is required'
          }
        }
      },

      // Adminy only
      customers: [],
      userId: null,

      // error handling
      errorDialog: false,
      errorTitle: 'Internal Error',
      isError: false,
      errorText: null
    }
  },
  computed: {
    ...mapGetters(['profile']),
    isAddMode () {
      return this.editedIndex < 0
    },
    isEditMode () {
      return this.editedIndex >= 0
    },
    noDataText () {
      return this.isLoading
        ? 'One moment...'
        : 'No queries found'
    },
    formTitle () {
      return this.isAddMode ? 'New Query' : 'Edit Query'
    },
    saveDisabled () {
      return (this.birthYears.length === 0 && this.editedQuery.id <= 0)
    },
    isAdmin () {
      return this.profile.roles !== null && this.profile.roles.includes('Admin')
    },
    isBreeder () {
      return this.profile.roles !== null && this.profile.roles.includes('Breeder')
    },
    isAdminy () {
      return this.isAdmin || this.isBreeder
    },
    isTemplateSelected () {
      return this.editedQuery.queryTypeId === this.queryTypeId_FromTemplate
    },
    isTemplate () {
      return !this.isNullOrUndefined(this.editedQuery.templateId)
        ? this.editedQuery.templateId.length > 0
        : false
    },
    animalSourceDisabled () {
      return this.editedIndex >= 0
    },
    queryTypeDisabled () {
      return this.editedQuery.animalSourceId.length === 0
        || this.editedIndex >= 0
    },
    templateDisabled () {
      return this.editedQuery.queryTypeId === this.queryTypeId_Custom
        || this.editedIndex >= 0
        || this.sourceTemplates.length === 0
    },
    breedDisabled () {
      if (this.isEditMode) return false
      return this.breeds.length === 0 
        || this.editedQuery.animalSourceId <= 0
        || parseInt(this.editedQuery.queryTypeId) === this.queryTypeId_FromTemplate
    },
    birthYearDisabled () {
      if (this.isEditMode) return false
      return this.birthYears.length === 0
        || this.editedQuery.animalSourceId <= 0
        || parseInt(this.editedQuery.queryTypeId) === this.queryTypeId_FromTemplate
    },
    titleDisabled () {
      return this.editedQuery.animalSourceId.length === 0
    },
    categoryDisabled () {
      return this.editedQuery.animalSourceId.length === 0
    },
    sourceTemplates () {
      if (this.editedQuery.animalSourceId.length === 0) return []
      const srcTemplates = this.animalSourcesTemplates
        .filter(t => { return t.animalSourceId === parseInt(this.editedQuery.animalSourceId) })

      var templates = srcTemplates.map(t => {
        return {
            value: t.id.toString(),
            text: t.title,
            description: t.description
        }
      })
      return templates
    },
    animalSourceItems () {
      return this.animalSources.map(c => {
        return { value: c.id.toString(), text: c.name }
      })
    },
    categoryItems () {
      return this.categories.map(c => {
        return {  value: c, text: c }
      })
    },
    selectedAnimalColourCode () {
      if (this.editedQuery.animalSourceId.length === 0) return this.appColor_Dark
      return this.getAnimalSourceColourCode(parseInt(this.editedQuery.animalSourceId))
    },
    isSmallDevice () {
      return this.$vuetify.breakpoint.name === 'xs' || this.$vuetify.breakpoint.name  === 'sm'
    },
    selectedCustomer () {
      const userId = this.userId
      const customers = this.customers.filter(u => { return u.value === userId })
      return customers.length > 0
        ? customers[0]
        : null
    },
    isCurrentUser () {
      return this.profile.id === this.userId
    }
  },
  methods: {
    loadData () {
      if (this.profile.id === undefined) this.$router.push('/')

      this.isLoading = true
      this.userId = this.profile.id

      if (!this.isAdminy) {
        this.loadQueries()
      } else {
        // default user_id might have been passed in
        if (this.isAdminy && this.$route.params.user_id !== undefined) {
          this.userId = this.$route.params.user_id      
        }
      }
    },

    loadQueries () {
      const self = this
      const isBreeder = self.isAdminy ? self.selectedCustomer.isBreeder : self.isBreeder
      appService.getBullFinderQueries(self.userId, isBreeder).then(data => {
        if (data.errorSeverity > 0) {
          self.isError = true
          self.errorText = data.errorMessage
          self.errorDialog = true
        } else {
          self.queries = data.queries
          self.animalSourcesTemplates = data.animalSourcesTemplates
          self.animalSourcesCriteria = data.animalSourcesCriteria
          self.animalSources = data.animalSources
          self.animalSourcesBreeds = data.animalSourcesBreeds
          self.animalSourcesYears = data.animalSourcesYears
          self.categories = data.categories
        }
      })

      self.isLoading = false
    },

    edit (item) {
      this.editedIndex = this.queries.indexOf(item)
      this.editedQuery = Object.assign({}, item)
      this.editedQuery.animalSourceId = item.animalSourceId.toString()
      this.editedQuery.template = Object.assign({}, this.defaultQuery.template)

      const sourceId = parseInt(item.animalSourceId)
      this.breeds = this.getBreeds(sourceId)
      this.birthYears = this.getBirthYears(sourceId)

      this.editedQuery.breedId = this.editedQuery.breedId.toString()
      this.editedQuery.birthYear = this.editedQuery.birthYear.toString()

      // if it's a template query, it was custom to start with
      if (this.editedQuery.queryTypeId === this.queryTypeId_Template) {
        this.editedQuery.queryTypeId = this.queryTypeId_Custom
      }

      if (typeof this.$refs.category !== 'undefined') {
        this.$refs.category.internalSearch = this.editedQuery.category
      }

      this.editDialog = true
    },

    remove (item) {
      const self = this
      const index = self.queries.indexOf(item)
      const queryTitle = self.queries[index].title
      
      self.$confirm('<p class="subtitle-1 pt-5">' + queryTitle + '</p>', {
        color: 'red darken-3',
        icon: 'help',
        title: 'Delete Query?',
        width: 300,
        persistent: true
      }).then(res => {
        if (res) {
          self.isSaving = true
          self.confirmDialog = false
          appService.deleteUserQuery(item.id).then(() => {
            self.queries.splice(index, 1)
            if (item.isTemplate) {
              self.removeFromTemplates(item.id)
            }
            self.isSaving = false
            self.editedIndex = -1
          })
        }
      }).catch(() => {
        self.isSaving = false
        self.editedIndex = -1
      })
    },

    saveEdit () {
      const self = this
      self.$validator.validateAll().then(valid => {
        if (valid) {
          self.isSaving = true
          if (self.isEditMode) {
            const updateModel = self.getUpdateModel(self.editedQuery)
            appService.updateUserQuery(self.editedQuery.id, updateModel, self.scope).then((data) => {
              self.isSaving = false
              if (data.errorSeverity > 0) {
                self.errorText = data.errorMessage
                self.errorSeverity = data.errorSeverity
                self.errorDialog = true
              } else {
                var result = data.userQuery
                const isTemplate = result.queryTypeId === self.queryTypeId_Template
                self.queries[self.editedIndex].title = result.title
                self.queries[self.editedIndex].description = result.description
                self.queries[self.editedIndex].category = result.category
                self.queries[self.editedIndex].breedId = result.breedId
                self.queries[self.editedIndex].birthYear = result.birthYear
                self.queries[self.editedIndex].queryTypeId = result.queryTypeId
                self.queries[self.editedIndex].isTemplate = isTemplate
                if (!isTemplate && self.isAdmin) {
                  self.removeFromTemplates(result.id)
                }
              }
              self.closeEditDialog()
              self.editedIndex = -1
            }).catch(() => {
              self.isSaving = false
              self.closeEditDialog()
              self.editedIndex = -1
            })
          } else {
            self.isAdding = true
            const addModel = self.getAddModel(self.editedQuery)
            self.addUserQuery(addModel).then((data) => {
              // if the animal source is not already local then add it
              const sourceId = data.query.animalSourceId
              if (self.animalSourcesCriteria.filter(src => { return src.id === sourceId }).length === 0) {
                self.animalSourcesCriteria.push({
                  id: sourceId,
                  filterCriteria: data.filterCriteria,
                  traitCriteria: data.traitCriteria
                })  
              }

              self.editedQuery = data.query
              self.queries.push(self.editedQuery)
              self.editedIndex = self.queries.length - 1
              self.isSaving = false
              self.closeEditDialog()

              if (addModel.queryTypeId === self.queryTypeId_Custom) {
                self.editDetail(self.editedQuery)
              }
            }).catch(() => {
              self.isSaving = false
              self.closeEditDialog()
              self.editedIndex = -1
            })
          }
        }
      })
    },

    addUserQuery (addModel) {
      const self = this
      return new Promise ((resolve, reject) => {
        if (addModel.queryTypeId === self.queryTypeId_Custom) {
          appService.addUserQuery(addModel).then((data) => {
            const newId = data.id
            if (newId > 0) {
              const filterModels = self.getFilterSelectionModels()
              appService.initQueryFilters(newId, filterModels, self.scope).then((data) => {
                resolve(data)
              }).catch(reject)
            }
          })
        } else if (addModel.queryTypeId === self.queryTypeId_FromTemplate) {
          appService.cloneTemplateQuery(addModel.templateId, addModel, self.isAdminy).then((data) => {
            resolve(data)
          }).catch(reject)
        }
      })
    },

    getAddModel (q) {
      return {
        userId: this.userId,
        title: this.trimText(q.title),
        description: this.trimText(q.description),
        category: this.trimText(this.$refs.category.internalSearch),
        animalSourceId: parseInt(q.animalSourceId),
        queryTypeId: q.queryTypeId,
        templateId: q.templateId.length > 0
          ? parseInt(q.templateId)
          : 0
      }
    },

    getUpdateModel (q) {
      if (this.isAdmin) {
        q.queryTypeId = q.isTemplate
          ? this.queryTypeId_Template
          : this.queryTypeId_Custom
      }

      return {
        id: q.id,
        userId: this.userId,
        title: this.trimText(q.title),
        description: this.trimText(q.description),
        category: this.trimText(this.$refs.category.internalSearch),
        queryTypeId: q.queryTypeId,
        birthYear: q.birthYear,
        breedId: q.breedId
      }
    },

    trimText(text) {
      return !this.isNullOrUndefined(text)
        ? text.length > 0 ? text.trim() : null
        : null
    },

    getFilterSelectionModels () {
      var result = [
        {
          columnName: this.filter_Breed,
          value: this.editedQuery.breedId
        },
        {
          columnName: this.filter_BirthYear,
          value: this.editedQuery.birthYear
        }
      ]
      return result
    },

    closeEditDialog () {
      const self = this
      self.editDialog = false
      setTimeout(() => {
        self.editedQuery = Object.assign({}, self.defaultQuery)
        self.editedIndex = -1
        self.$validator.reset()
        if (typeof this.$refs.category !== 'undefined') {
          this.$refs.category.internalSearch = null
        }
      }, 300)
    },

    editDetail (item) {
      const self = this

      // will be 'undefined' if called after add new
      if (typeof item !== 'undefined') {
        self.editedIndex = self.queries.indexOf(item)
      }
      if (self.editedIndex >= 0) {
        const sourceId = parseInt(item.animalSourceId)
        const idx = self.editedIndex

        self.breeds = self.getBreeds(sourceId)
        self.birthYears = self.getBirthYears(sourceId)
        self.filterCriteriaOriginal = []
        self.traitCriteriaOriginal = []
        self.filterCriteriaOriginal = self.cloneFilterCriteria(sourceId, self.queries[idx].filterSelections)
        self.traitCriteriaOriginal = self.cloneTraitCriteria(sourceId, self.queries[idx].traitIds)
        self.selectedTraitIds = self.queries[idx].traitIds.map(id => id)
        self.selectedQueryInfo = {
          id: self.queries[idx].id,
          title: self.queries[idx].title,
          description: self.queries[idx].description,
          colourCode: self.selectedColourCode = self.getAnimalSourceColourCode(sourceId),
          cosmosCode: self.getAnimalSourceCosmosCode(sourceId)     
        }
        
        self.detailDialog = true
      }
    },

    cloneTraitCriteria (animalSourceId, traitIds) {
      var result = []
      const traitCriteria = this.getSourceTraitCriteria(animalSourceId)

      for (var key1 in traitCriteria) {
        var traits = []
        const groupTraits = traitCriteria[key1].traits
        for (var key2 in groupTraits) {
          traits.push({
            id: groupTraits[key2].id,
            description: groupTraits[key2].description,
            sequence: groupTraits[key2].sequence,
            isBreeder: groupTraits[key2].isBreeder,
            selected: traitIds.includes(groupTraits[key2].id)
          })
        }
        result.push({
          id: traitCriteria[key1].id,
          groupName: traitCriteria[key1].groupName,
          sequence: traitCriteria[key1].sequence,
          traits: traits
        })
      }
      return result
    },

    cloneFilterCriteria (animalSourceId, filterSelections) {
      const self = this
      var result = []
      const filters = self.getSourceFilterCriteria(animalSourceId)
        
      filters.forEach(f => {
        var dropdownItems = []
        const filterDropdowns = f.dropdownItems
        filterDropdowns.forEach(dd => {
          dropdownItems.push({
            value: dd.value,
            text: dd.text
          })
        })
        const filterColumn = f.columnName
        const filterValue = filterSelections.filter(f => { return f.columnName === filterColumn })[0].value

        result.push({
          id: f.id,
          description: f.description,
          inputTypeId: f.inputTypeId,
          columnName: filterColumn,
          minValue: filterValue,
          maxValue: filterValue,
          isDisabled: false,
          dropdownItems: dropdownItems
        })
      })
      return result
    },

    criteriaSave ({ userQueries }) {
      const idx = this.editedIndex >= 0
        ? this.editedIndex
        : userQueries.length - 1

      this.queries = userQueries
      this.traitCriteriaOriginal = []
      this.filterCriteriaOriginal = []

      const sourceId = this.queries[idx].animalSourceId
      this.filterCriteriaOriginal = this.cloneFilterCriteria(sourceId, this.queries[idx].filterSelections)
      this.traitCriteriaOriginal = this.cloneTraitCriteria(sourceId, this.queries[idx].traitIds)
      this.detailDialog = false
      this.editedIndex = -1
    },

    criteriaCancel () {
      this.detailDialog = false
      this.editedIndex = -1
    },

    getSourceFilterCriteria (sourceId) {
      const sources = this.animalSourcesCriteria.filter(src => { 
        return src.id === sourceId 
      })

      return sources.length > 0
        ? sources[0].filterCriteria
        : []
    },

    getSourceTraitCriteria (sourceId) {
      const sources = this.animalSourcesCriteria.filter(src => { 
        return src.id === sourceId 
      })

      return sources.length > 0
        ? sources[0].traitCriteria
        : []
    },

    getBirthYears (sourceId) {
      const sources = this.animalSourcesYears.filter(src => { return src.animalSourceId === sourceId })
      return sources.length > 0
        ? sources[0].birthYears
        : []
    },

    getBreeds (sourceId) {
      const sources = this.animalSourcesBreeds.filter(src => { return src.animalSourceId === sourceId })
      return sources.length > 0
        ? sources[0].breeds
        : []
    },

    getBreedName (sourceId, breedId) {
      const sources = this.animalSourcesBreeds.filter(src => { return src.animalSourceId === parseInt(sourceId) })

      const breeds = sources.length > 0
        ? sources[0].breeds.filter(b => { return parseInt(b.value) === parseInt(breedId) })
        : []

      return breeds.length > 0
        ? breeds[0].text
        : 'Unknown'
    },

    getAnimalSourceName (id) {
      const sourceId = parseInt(id)
      const sources = this.animalSources.filter(s => { return s.id === sourceId })
      return sources.length > 0
        ? sources[0].name
        : 'Unknown'
    },

    getAnimalSourceColourCode (id) {
      const sourceId = parseInt(id)
      const sources = this.animalSources.filter(s => { return s.id === sourceId })
      return sources.length > 0
        ? sources[0].colourCode
        : 'grey'
    },

    getAnimalSourceCosmosCode (id) {
      const sourceId = parseInt(id)
      const sources = this.animalSources.filter(s => { return s.id === sourceId })
      return sources.length > 0
        ? sources[0].cosmosCode
        : ''
    },

    animalSourceChanged () {
      const sourceId = parseInt(this.editedQuery.animalSourceId)
      this.breeds =  this.getBreeds(sourceId)
      this.birthYears = this.getBirthYears(sourceId)
      this.editedQuery.breedId = this.breeds.length > 0
        ? this.breeds[0].value.toString()
        : ''
      this.editedQuery.birthYear = this.birthYears.length > 0
        ? this.birthYears[0].value
        : ''
        
      if (this.sourceTemplates.length === 0) {
        this.editedQuery.queryTypeId = this.queryTypeId_Custom
      }
      this.$validator.reset()
      // this.setFocus(this.$refs.category)
    },

    queryTypeChanged () {
      if (this.editedQuery.animalSourceId.length === 0) return

      if (this.editedQuery.queryTypeId === this.queryTypeId_FromTemplate) {
        this.templateChanged()
      } else {
        this.editedQuery.templateId = ''
        this.editedQuery.templateDesc = ''
        this.editedQuery.title = null
        this.editedQuery.description = null
        this.$validator.reset()
      }
    },

    templateChanged () {
      if (this.editedQuery.animalSourceId.length === 0) return

      const templateId = this.editedQuery.templateId.length > 0
        ? parseInt(this.editedQuery.templateId)
        : null

      const templates = this.animalSourcesTemplates.filter(t => { return t.id === templateId })
      if (templates.length > 0) {
        this.editedQuery.templateId = templates[0].id.toString()
        this.editedQuery.templateDesc = templates[0].description
        this.editedQuery.title = templates[0].title
        this.editedQuery.description = templates[0].description
        this.editedQuery.breedId = templates[0].breedId.toString()
        this.editedQuery.birthYear = templates[0].birthYear.toString()

        this.setFocus(this.$refs.title)
        this.$validator.reset()

        if (this.editedQuery.queryTypeId !== this.queryTypeId_FromTemplate) {
          this.editedQuery.templateId = ''
          this.editedQuery.templateDesc = ''
        }
      }
    },

    breedOrYearChanged () {
      // this.setFocus(this.$refs.title)
    },

    removeFromTemplates (id) {
      const templates = this.animalSourcesTemplates.filter(t => { return t.id === id })
      if (templates.length > 0) {
        const idx = this.animalSourcesTemplates.indexOf(templates[0])
        this.animalSourcesTemplates.splice(idx, 1)
      }
    },

    setFocus (input) {
      setTimeout(() => {
        input.focus()
      }, 300)
    },

    isNullOrUndefined (obj) {
      return obj === undefined 
        || typeof obj === 'undefined' 
        || obj === null
    },

    // event handlers
    customersLoaded ({ customers }) {
      this.customers = customers
      this.loadQueries()
    },

    customerChanged ({ customerId }) {
      this.userId = customerId
      this.loadQueries()
    },

    customerError ({ error }) {
      if (error.severity > 0) {
        this.isError = true
        this.errorTitle = 'Customer Load Error'
        this.errorText = error.message
        this.errorDialog = true
      }
    },

    actionLinkClicked () {
      this.$router.push(this.isCurrentUser
        ? '/bullFinder'
        : `/bullFinder/${this.userId}`)
    }
  },
  created () {
    this.loadData()
  },
  mounted () {
    this.$validator.localize('en', this.dictionary)
  },
  watch: {
    editDialog (val) {
      val || this.closeEditDialog()
    }
  }
}
</script>